import React, { useState } from 'react';
import { ChapterPlan, Chapter } from '../src/types';
import { X, ChevronDown, ChevronRight, Lock, Unlock, ArrowUp, ArrowDown, Plus, Trash2, RefreshCcw, Save } from 'lucide-react';
import Tooltip from './Tooltip';

import { RoadmapWarning } from '../src/types';
interface RoadmapEditorProps {
  warnings?: RoadmapWarning[];
  isOpen: boolean;
  onClose: () => void;
  plans: ChapterPlan[];
  targetChapterCount: number;
  chapters: Chapter[];
  onSave: (newPlans: ChapterPlan[], newTargetCount: number) => Promise<void>;
  onRegenerateRemaining: (fromIndex: number, currentPlans: ChapterPlan[], targetChapterCount: number) => Promise<void>;
  onRegenerateChapter: (index: number, currentPlans: ChapterPlan[]) => Promise<void>;
}

export default function RoadmapEditor({
  isOpen, onClose, plans: initialPlans, warnings, targetChapterCount: initialCount, chapters, onSave, onRegenerateRemaining, onRegenerateChapter
}: RoadmapEditorProps) {
  const [plans, setPlans] = useState<ChapterPlan[]>(JSON.parse(JSON.stringify(initialPlans)));
  const [targetCount, setTargetCount] = useState<number>(initialCount);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [acknowledgedWarnings, setAcknowledgedWarnings] = useState<number[]>([]);

  React.useEffect(() => {
    if (isOpen) {
      setPlans(JSON.parse(JSON.stringify(initialPlans)));
      setTargetCount(initialCount);
      setExpandedIndex(null);
    }
  }, [isOpen, initialPlans, initialCount]);

  // keep track of changes
  const hasChanges = JSON.stringify(plans) !== JSON.stringify(initialPlans) || targetCount !== initialCount;

  if (!isOpen) return null;

  const handleClose = () => {
    if (hasChanges) {
      if (!window.confirm("You have unsaved roadmap changes. Leave without saving?")) {
        return;
      }
    }
    onClose();
  };

  const handleSave = async () => {
    setIsSaving(true);
    await onSave(plans, targetCount);
    setIsSaving(false);
    onClose();
  };

  const updatePlan = (index: number, updates: Partial<ChapterPlan>) => {
    const newPlans = [...plans];
    newPlans[index] = { ...newPlans[index], ...updates };
    setPlans(newPlans);
  };

  const updateStringArray = (index: number, field: keyof ChapterPlan, arrIndex: number, value: string) => {
    const newPlans = [...plans];
    const arr = [...(newPlans[index][field] as string[] || [])];
    arr[arrIndex] = value;
    newPlans[index] = { ...newPlans[index], [field]: arr };
    setPlans(newPlans);
  };

  const addStringArrayItem = (index: number, field: keyof ChapterPlan) => {
    const newPlans = [...plans];
    const arr = [...(newPlans[index][field] as string[] || [])];
    arr.push('');
    newPlans[index] = { ...newPlans[index], [field]: arr };
    setPlans(newPlans);
  };

  const removeStringArrayItem = (index: number, field: keyof ChapterPlan, arrIndex: number) => {
    const newPlans = [...plans];
    const arr = [...(newPlans[index][field] as string[] || [])];
    arr.splice(arrIndex, 1);
    newPlans[index] = { ...newPlans[index], [field]: arr };
    setPlans(newPlans);
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    const newPlans = [...plans];
    const temp = newPlans[index];
    newPlans[index] = newPlans[index - 1];
    newPlans[index - 1] = temp;
    
    // Update chapterNumbers
    newPlans[index].chapterNumber = index + 1;
    newPlans[index - 1].chapterNumber = index;
    
    setPlans(newPlans);
  };

  const moveDown = (index: number) => {
    if (index === plans.length - 1) return;
    const newPlans = [...plans];
    const temp = newPlans[index];
    newPlans[index] = newPlans[index + 1];
    newPlans[index + 1] = temp;
    
    // Update chapterNumbers
    newPlans[index].chapterNumber = index + 1;
    newPlans[index + 1].chapterNumber = index + 2;
    
    setPlans(newPlans);
  };

  const addChapter = () => {
    setPlans([...plans, {
      id: `plan_manual_${Date.now()}`,
      chapterNumber: plans.length + 1,
      title: 'New Chapter',
      summary: '',
      requiredBeats: [],
      status: 'planned' as const,
      updatedAt: new Date().toISOString()
    }]);
    setTargetCount(targetCount + 1);
  };

  const deleteChapter = (index: number) => {
    if (window.confirm("Are you sure you want to delete this chapter plan?")) {
      const newPlans = plans.filter((_, i) => i !== index).map((p, i) => ({ ...p, chapterNumber: i + 1 }));
      setPlans(newPlans);
      setTargetCount(targetCount - 1);
    }
  };

  const hasGeneratedContent = (plan: ChapterPlan) => {
    return plan.status === 'drafted' || plan.status === 'locked' || chapters.some(c => c.id === plan.chapterNumber);
  };

  const handleTargetCountChange = (newCount: number) => {
    if (newCount < 1) newCount = 1;
    if (newCount < plans.length) {
      // Trying to decrease
      const chaptersToDelete = plans.length - newCount;
      const lastChapters = plans.slice(-chaptersToDelete);
      
      const hasGenerated = lastChapters.some(p => hasGeneratedContent(p));
      if (hasGenerated) {
        if (!window.confirm("Some of the chapters you are about to remove already have drafted content or are locked. Only proceed if you explicitly want to delete their roadmap plans (content will remain in the manuscript). Continue?")) {
          return;
        }
      }
      setPlans(plans.slice(0, newCount));
    } else if (newCount > plans.length) {
      // Trying to increase
      const chaptersToAdd = newCount - plans.length;
      const addedPlans = Array.from({ length: chaptersToAdd }).map((_, i) => ({
        id: `plan_extend_${Date.now()}_${i}`,
        chapterNumber: plans.length + i + 1,
        title: `Chapter ${plans.length + i + 1}`,
        summary: '',
        requiredBeats: [],
        status: 'planned' as const,
        updatedAt: new Date().toISOString()
      }));
      setPlans([...plans, ...addedPlans]);
    }
    setTargetCount(newCount);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-cv-panel border border-cv-border-strong rounded-xl w-full max-w-5xl h-full max-h-[90vh] shadow-2xl flex flex-col flex-1">
        <div className="flex items-center justify-between p-4 border-b border-cv-border">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-black text-cv-primary uppercase tracking-tighter flex items-center gap-3">
              Roadmap Editor
              <Tooltip text="The Chapter Roadmap defines what happens in each chapter. The AI adheres to this plan, so ensure it describes the specific events you want." />
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-cv-muted">
              Target Chapters:
              <input 
                type="number" 
                value={targetCount} 
                onChange={(e) => handleTargetCountChange(parseInt(e.target.value) || 1)}
                className="w-16 bg-cv-page border border-cv-border rounded px-2 py-1 text-cv-primary focus:border-cv-interactive outline-none"
              />
            </div>
            <button onClick={handleSave} disabled={isSaving} className="px-4 py-2 bg-cv-interactive hover:bg-cv-interactive-hover text-cv-page rounded font-bold uppercase text-xs flex items-center gap-2">
              <Save size={14} /> Save Changes
            </button>
            <button onClick={handleClose} className="p-2 text-cv-muted hover:text-cv-primary rounded-lg hover:bg-cv-elevated">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
          <div className="flex flex-col gap-3">
            {plans.map((plan, index) => {
              const isExpanded = expandedIndex === index;
              const hasContent = hasGeneratedContent(plan);
              const isLocked = plan.status === 'locked';

              return (
                <div key={plan.id} className={`border ${isExpanded ? 'border-cv-interactive/50' : 'border-cv-border'} rounded-lg bg-cv-page/50 overflow-hidden transition-all`}>
                  {/* Header */}
                  <div className="flex items-center p-3 gap-3 bg-cv-panel/50">
                    <button onClick={() => setExpandedIndex(isExpanded ? null : index)} className="text-cv-muted hover:text-cv-primary">
                      {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    </button>
                    
                    <div className="flex-1 flex gap-4 items-center">
                      <span className="text-cv-gold font-black min-w-10">CH {plan.chapterNumber}</span>
                      <input 
                        type="text" 
                        value={plan.title} 
                        onChange={e => updatePlan(index, { title: e.target.value })}
                        className="bg-transparent text-cv-primary font-bold text-sm focus:outline-none focus:border-b focus:border-cv-interactive flex-1 truncate max-w-sm"
                        disabled={isLocked}
                      />
                      {hasContent && (
                        <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-widest bg-green-500/20 text-green-400">Drafted</span>
                      )}
                    </div>

                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => updatePlan(index, { status: isLocked ? 'planned' : 'locked' })}
                        className={`p-1.5 rounded ${isLocked ? 'text-cv-danger hover:bg-red-500/20' : 'text-cv-muted hover:text-cv-primary hover:bg-cv-elevated'}`}
                        title={isLocked ? "Unlock Plan" : "Lock Plan"}
                      >
                        {isLocked ? <Lock size={16} /> : <Unlock size={16} />}
                      </button>
                      <button onClick={() => moveUp(index)} disabled={index === 0 || isLocked} className="p-1.5 text-cv-muted hover:text-cv-primary hover:bg-cv-elevated disabled:opacity-30 rounded">
                        <ArrowUp size={16} />
                      </button>
                      <button onClick={() => moveDown(index)} disabled={index === plans.length - 1 || isLocked} className="p-1.5 text-cv-muted hover:text-cv-primary hover:bg-cv-elevated disabled:opacity-30 rounded">
                        <ArrowDown size={16} />
                      </button>
                      <button onClick={() => deleteChapter(index)} disabled={isLocked || hasContent} className="p-1.5 text-cv-danger/50 hover:text-cv-danger hover:bg-red-500/20 disabled:opacity-30 rounded ml-2">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="p-4 border-t border-cv-border flex flex-col gap-4">
                      {hasContent && !isLocked && (
                        <div className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-300/80 text-xs p-3 rounded flex items-start gap-2">
                          <span className="text-yellow-400 font-bold">!</span>
                          <p>This chapter already has manuscript text. Editing its plan here will NOT automatically rewrite the chapter unless you explicitly revise/regenerate it later.</p>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-[100px_1fr] gap-4 items-start">
                        <label className="text-xs uppercase tracking-widest text-cv-muted font-black mt-2">POV</label>
                        <input 
                          type="text" 
                          value={plan.pov || ''} 
                          onChange={e => updatePlan(index, { pov: e.target.value })}
                          className="bg-cv-panel border border-cv-border-strong rounded p-2 text-sm text-cv-secondary focus:outline-none focus:border-cv-interactive"
                          disabled={isLocked}
                          placeholder="Character POV..."
                        />
                      </div>

                      <div className="grid grid-cols-[100px_1fr] gap-4 items-start">
                        <label className="text-xs uppercase tracking-widest text-cv-muted font-black mt-2">Summary</label>
                        <textarea 
                          value={plan.summary} 
                          onChange={e => updatePlan(index, { summary: e.target.value })}
                          className="bg-cv-panel border border-cv-border-strong rounded p-2 text-sm text-cv-secondary focus:outline-none focus:border-cv-interactive min-h-[80px]"
                          disabled={isLocked}
                        />
                      </div>

                      <div className="grid grid-cols-[100px_1fr] gap-4 items-start">
                        <label className="text-xs uppercase tracking-widest text-cv-muted font-black mt-2">Beats</label>
                        <div className="flex flex-col gap-2">
                          {(plan.requiredBeats || []).map((beat, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <textarea 
                                value={beat} 
                                onChange={e => updateStringArray(index, 'requiredBeats', i, e.target.value)}
                                className="bg-cv-panel border border-cv-border-strong rounded p-1.5 text-xs text-cv-secondary focus:outline-none focus:border-cv-interactive flex-1 min-h-[40px]"
                                disabled={isLocked}
                              />
                              {!isLocked && (
                                <button onClick={() => removeStringArrayItem(index, 'requiredBeats', i)} className="text-cv-danger p-1 hover:bg-red-500/20 rounded mt-1">
                                  <Trash2 size={12} />
                                </button>
                              )}
                            </div>
                          ))}
                          {!isLocked && (
                            <button onClick={() => addStringArrayItem(index, 'requiredBeats')} className="text-xs font-bold uppercase text-cv-gold hover:text-cv-interactive flex items-center gap-1 self-start mt-1">
                              <Plus size={12} /> Add Beat
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-[100px_1fr] gap-4 items-start">
                        <label className="text-xs uppercase tracking-widest text-cv-muted font-black mt-2">Forbidden</label>
                        <div className="flex flex-col gap-2">
                          {(plan.forbiddenMoves || []).map((move, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <textarea 
                                value={move} 
                                onChange={e => updateStringArray(index, 'forbiddenMoves', i, e.target.value)}
                                className="bg-cv-panel border border-cv-border-strong rounded p-1.5 text-xs text-cv-secondary focus:outline-none focus:border-cv-danger flex-1 min-h-[40px]"
                                disabled={isLocked}
                              />
                              {!isLocked && (
                                <button onClick={() => removeStringArrayItem(index, 'forbiddenMoves', i)} className="text-cv-danger p-1 hover:bg-red-500/20 rounded mt-1">
                                  <Trash2 size={12} />
                                </button>
                              )}
                            </div>
                          ))}
                          {!isLocked && (
                            <button onClick={() => addStringArrayItem(index, 'forbiddenMoves')} className="text-xs font-bold uppercase text-cv-danger hover:text-cv-danger flex items-center gap-1 self-start mt-1">
                              <Plus size={12} /> Add Forbidden Move
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-[100px_1fr] gap-4 items-start">
                        <label className="text-xs uppercase tracking-widest text-cv-muted font-black mt-2">Continuity</label>
                        <div className="flex flex-col gap-2">
                          {(plan.continuityNotes || []).map((note, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <textarea 
                                value={note} 
                                onChange={e => updateStringArray(index, 'continuityNotes', i, e.target.value)}
                                className="bg-cv-panel border border-cv-border-strong rounded p-1.5 text-xs text-cv-secondary focus:outline-none focus:border-cv-warning flex-1 min-h-[40px]"
                                disabled={isLocked}
                              />
                              {!isLocked && (
                                <button onClick={() => removeStringArrayItem(index, 'continuityNotes', i)} className="text-cv-danger p-1 hover:bg-red-500/20 rounded mt-1">
                                  <Trash2 size={12} />
                                </button>
                              )}
                            </div>
                          ))}
                          {!isLocked && (
                            <button onClick={() => addStringArrayItem(index, 'continuityNotes')} className="text-xs font-bold uppercase text-cv-warning hover:text-cv-warning flex items-center gap-1 self-start mt-1">
                              <Plus size={12} /> Add Continuity Note
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                          <label className="text-xs uppercase tracking-widest text-cv-muted font-black">Character Focus</label>
                          <input 
                            type="text" 
                            value={(plan.characterFocus || []).join(', ')} 
                            onChange={e => updatePlan(index, { characterFocus: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                            className="bg-cv-panel border border-cv-border-strong rounded p-2 text-xs text-cv-secondary focus:outline-none focus:border-cv-interactive"
                            disabled={isLocked}
                            placeholder="Comma separated..."
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="text-xs uppercase tracking-widest text-cv-muted font-black">Location Focus</label>
                          <input 
                            type="text" 
                            value={(plan.locationFocus || []).join(', ')} 
                            onChange={e => updatePlan(index, { locationFocus: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                            className="bg-cv-panel border border-cv-border-strong rounded p-2 text-xs text-cv-secondary focus:outline-none focus:border-cv-interactive"
                            disabled={isLocked}
                            placeholder="Comma separated..."
                          />
                        </div>
                      </div>

                      <div className="pt-4 mt-2 border-t border-cv-border/50 flex justify-between items-center bg-cv-page px-2 py-2 -mx-4 -mb-4 rounded-b-lg">
                        <span className="text-[10px] text-cv-muted font-mono">ID: {plan.id}</span>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => {
                              onRegenerateChapter(index, plans);
                            }} 
                            disabled={isLocked}
                            className="text-xs uppercase font-bold tracking-wider px-3 py-1.5 rounded bg-cv-interactive/15 text-cv-gold border border-cv-interactive/20 hover:bg-cv-interactive-hover/20 transition-colors disabled:opacity-30 flex items-center gap-2"
                          >
                            <RefreshCcw size={12} /> Regen Ch {plan.chapterNumber}
                          </button>
                          <button 
                            onClick={() => {
                              if(window.confirm("Regenerate the future roadmap starting from THIS chapter? Existing locked/drafted earlier chapters won't be modified.")) {
                                onRegenerateRemaining(index, plans, targetCount);
                              }
                            }} 
                            disabled={isLocked}
                            className="text-xs uppercase font-bold tracking-wider px-3 py-1.5 rounded bg-cv-gold/10 text-cv-interactive border border-cv-gold/20 hover:bg-cv-gold/20 transition-colors disabled:opacity-30 flex items-center gap-2"
                          >
                            <RefreshCcw size={12} /> Regen Future Roadmap
                          </button>
                        </div>
                      </div>

                    </div>
                  )}
                </div>
              );
            })}
            <button onClick={addChapter} className="w-full py-4 border border-dashed border-cv-border-strong hover:border-cv-border-strong rounded-lg text-cv-muted hover:text-cv-secondary font-bold uppercase tracking-widest text-sm flex items-center justify-center gap-2 transition-colors transition-all mt-2">
              <Plus size={18} /> Add Planned Chapter
            </button>
            <button 
              onClick={() => {
                if(window.confirm("Are you sure you want to let the AI regenerate the ENTIRE roadmap from scratch? Any locked or drafted chapters will remain safe, but everything else will be overwritten.")) {
                  onRegenerateRemaining(0, plans, targetCount);
                }
              }} 
              className="w-full py-4 border border-dashed border-cv-border-strong hover:border-cv-gold rounded-lg text-cv-interactive/70 hover:text-cv-interactive font-bold uppercase tracking-widest text-sm flex items-center justify-center gap-2 transition-colors mt-2 bg-cv-gold/10"
            >
              <RefreshCcw size={18} /> Regenerate Unlocked Chapters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
